import { AppBar, makeStyles, Tab } from '@material-ui/core';
import TabContext from '@material-ui/lab/TabContext';
import TabList from '@material-ui/lab/TabList';
import AppsIcon from '@material-ui/icons/Apps';
import SettingsIcon from '@material-ui/icons/Settings';
import TimelineIcon from '@material-ui/icons/Timeline';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { hasVisibleGlobalAhu } from '../Hooks/useVisibleGlobalAhu';
import { supportsDatabaseHistory } from '../Utils/database_history_utils';
import usePreviewFeatures from '../Hooks/usePreviewFeatures';

const useStyles = makeStyles((theme) => ({
  withDivider: { borderRight: `1px solid ${theme.palette.text.primary}` }
}));

// The same controller navigation stays available on stored history, including
// offline reloads where no live settings or zones have arrived yet.
export default function ControllerNavigation({
  controller,
  overviewVisible = hasVisibleGlobalAhu(controller)
}) {
  const { id, menu = 'zones', zone_id = 'zone_0', tab_id = 'info' } = useParams();
  const { t } = useTranslation();
  const classes = useStyles();
  const previewEnabled = usePreviewFeatures();
  const showData = controller.type === 5155 && supportsDatabaseHistory(controller, undefined, previewEnabled);
  const panelValue = menu === 'zones' ? zone_id : menu;
  const values = [
    'zoneoverview',
    'manage_zones',
    ...(showData ? ['database_history'] : []),
    ...Object.values(controller.zones ?? {}).map((zone) => `zone_${zone.id}`)
  ];
  const mainTabValue = values.includes(panelValue) ? panelValue : 'manage_zones';
  return (
    <TabContext value={mainTabValue}>
      <AppBar position="static" color="default">
        <TabList variant="scrollable" scrollButtons="auto">
          {[
            <Tab
              value={showData ? "database_history" : "zoneoverview"}
              label={
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  {showData ? <TimelineIcon /> : <><AppsIcon /> {t('overview')}</>}
                </div>
              }
              component={Link}
              aria-label={t(showData ? 'database_dashboard' : 'overview')}
              title={t(showData ? 'database_dashboard' : 'overview')}
              className={classes.withDivider}
              to={`/controller/${controller.id}/${showData ? 'database_history' : 'zoneoverview'}`}
              key={`overview-tab`}
              id={`overview-tab`}
              aria-controls={`overview-tabpanel`}
              /* due to this bug we cannot use conditional rendering inside TabList
                so we use this hack to hide the tab when only one zone is present
                https://github.com/mui/material-ui/issues/26166
              */
              style={{
                display:
                  showData || overviewVisible || Object.keys(controller.zones ?? {}).length > 1
                    ? 'flex'
                    : 'none'
              }}
            />,

            ...Object.values(controller.zones ?? {}).map((zone, index) => (
              <Tab
                value={'zone_' + zone.id}
                label={zone.name}
                component={Link}
                className={
                  index === Object.keys(controller.zones ?? {}).length - 1
                    ? classes.withDivider
                    : ''
                }
                to={`/controller/${id}/zones/zone_${zone.id}/${tab_id ?? 'info'}`}
                key={`zone_${zone.id}`}
                id={`zone_${zone.id}`}
                aria-controls={`zone-tabpanel-${index}`}
              />
            )),

            <Tab
              value="manage_zones"
              label={
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <SettingsIcon />
                </div>
              }
              component={Link}
              to={`/controller/${id}/${showData ? 'global_info' : 'manage_zones'}`}
              key={`manage_zones-tab`}
              id={`manage_zones-tab`}
              aria-controls={`manage_zones-tabpanel`}
              aria-label={t('system')}
            />

          ]}
        </TabList>
      </AppBar>
    </TabContext>
  );
}
